import React, { Component } from 'react';
import 'core-js/actual';
import 'regenerator-runtime/runtime';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { Route, Redirect, Switch } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import { createBrowserHistory } from 'history';
import ReduxToastr from 'react-redux-toastr';
import svgxuse from 'svgxuse';

import {
  getSiteConfig,
  proceedWithClient,
  rememberPathBeforeLogin,
  externalLogin,
  logoutUser,
  getSitePreload
} from 'actions';

import { SiteReadyState, AuthMode, AuthState } from 'actions/auth/common';

import PreloadBrands from 'common/App/Preload';
import Login from 'containers/Login';
import AdvancedSearch from 'containers/AdvancedSearch';
import AdvancedSearchMultiDb from 'containers/AdvancedSearchMultiDb';
import Home from 'containers/Home';
import ConnectionStatus from 'components/Common/ConnectionStatus';
import ValidationResults from 'containers/ValidationResults';
import OnlineForms from 'containers/OnlineForms';
import Blank from 'containers/Blank';
import OriginalPdfForm from 'containers/OriginalPdfForm';
import MIDashboard from 'containers/MIDashboard';
import Questionnaire from 'containers/Questionnaire';
import SupportingDocumentViewer from 'containers/SupportingDocumentViewer';
import AccountPage from 'containers/Account';
import CustomerPage from 'containers/Customer';
import Monitoring from 'containers/Monitoring';
import BatchesContainer from 'containers/BatchesContainer';
import BatchFilesContainer from 'containers/BatchFilesContainer';
import BatchFormsContainer from 'containers/BatchFormsContainer';
import ClientSelect from 'containers/ClientSelect';
import UnauthorizedPage from 'components/Errors/UnauthorizedPage';
import UnlicensedPage from 'components/Errors/UnlicensedPage';
import NoInternetPage from 'components/Errors/NoInternetPage';
import NotFoundPage from 'components/Errors/NotFoundPage';
import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';
import Header from 'components/Header';
import VisualiseW8Imy from 'components/VisualiseW8Imy';
import WhdStatement from 'components/WhdStatement';
import SiteLoading from 'components/SiteLoading';
import withAppErrorBoundary from 'components/ErrorBoundaries/AppErrorBoundary/withAppErrorBoundary';
import AddCustomerAccount from 'containers/AddCustomerAccount';
import WhsManagement from 'containers/WhsManagement/index.js';

import '../../scss/spectre/spectre.scss';
import '../../scss/spectre/spectre-icons.scss';
import '../../scss/app.global.scss';
import '../../scss/branding/taina.scss';

const history = createBrowserHistory();
export { history };
export { svgxuse };

class App extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    siteReady: PropTypes.string.isRequired,
    authMode: PropTypes.string.isRequired,
    sessionAuthMode: PropTypes.string.isRequired,
    authState: PropTypes.string,
    isAuthenticated: PropTypes.bool.isRequired,
    username: PropTypes.string,
    settings: PropTypes.shape(),
    permissions: PropTypes.arrayOf(PropTypes.string),
    isFetchingUserConfig: PropTypes.bool
  };

  static defaultProps = {
    username: null,
    settings: null,
    permissions: null,
    isFetchingUserConfig: false,
    authState: AuthState.Unknown,
  };

  static Brand = process.env.brand;

  constructor(props) {
    super(props);

    this.monitorRequests = this.monitorRequests.bind(this);
  }

  componentDidMount() {
    const { dispatch } = this.props;

    if (PreloadBrands.includes(App.Brand)) {
      this.preloadCheck();
    } else {
      dispatch(getSiteConfig());
    }

    window.addEventListener('storage', this.monitorRequests);
    document.addEventListener('tainaSessionReset', this.monitorRequests);
  }

  componentWillUnmount() {
    window.removeEventListener('storage', this.monitorRequests);
    document.removeEventListener('tainaSessionReset', this.monitorRequests);
  }

  get clientIsSet() {
    const { settings } = this.props;
    return settings != null && (settings.selectedClient != null || (settings.client == null && settings.clients.length <= 1));
  }

  get isSiteLoaded() {
    const { siteReady, authState } = this.props;
    return siteReady === SiteReadyState.Ready && (authState === AuthState.Known || authState === AuthState.LoggedOut || authState === AuthState.Unauthorised);
  }

  get isSiteDown() {
    const { siteReady } = this.props;
    return siteReady === SiteReadyState.Down;
  }

  get siteHasNoClients() {
    const { siteReady } = this.props;
    return siteReady === SiteReadyState.NoClients;
  }

  get isAuthenticatedUser() {
    const { isAuthenticated, authState } = this.props;
    return this.isSiteLoaded && isAuthenticated && authState === AuthState.Known;
  }

  get isUnauthorized() {
    const { authState } = this.props;
    const { location: { pathname } } = history;
    return authState === AuthState.Unauthorised || ['/unauthorized'].indexOf(pathname) !== -1;
  }

  get isUnlicensed() {
    const { siteReady } = this.props;
    return siteReady === SiteReadyState.Unlicensed;
  }

  monitorRequests(event) {
    if (event.key === 'taina.logout') {
      this.logout();
    }
    if (event.key === 'taina.reset') {
      this.reset();
    }
  }

  logout() {
    const { dispatch, settings, sessionAuthMode } = this.props;
    const switchClient = sessionAuthMode === AuthMode.SSO && settings && settings.clients && settings.clients.length !== 1;
    dispatch(logoutUser(switchClient, false));
    localStorage.removeItem('taina.logout');
  }

  reset() {
    const { dispatch } = this.props;

    dispatch(logoutUser(false, false));
    localStorage.removeItem('taina.reset');
  }

  proceedWithClientSelection() {
    const { dispatch, settings } = this.props;
    const { location: { pathname } } = history;
    if (!settings || !settings.clients) {
      return null;
    }

    if (!settings.selectedClient) {
      if (settings.clients.length === 1) {
        dispatch(proceedWithClient(settings.clients[0]));
        return <SiteLoading />;
      }
      if (settings.clients.length > 1 && ['/client-select'].indexOf(pathname) === -1) {
        return <Redirect to="/client-select" />;
      }
    }
    return null;
  }

  redirectToLogin() {
    const { dispatch, authMode, authState } = this.props;
    const { location: { pathname } } = history;

    if (authMode !== AuthMode.Manual && authState === AuthState.LoggedOut) {
      return <Redirect to="/sign-in" />;
    }

    if (['/sign-in', '/unauthorized'].indexOf(pathname) === -1) {
      if (pathname !== '/') {
        dispatch(rememberPathBeforeLogin(pathname));
      }
      return (
        <Redirect to="/sign-in" />
      );
    }
    return null;
  }

  redirectToSSO() {
    const { dispatch } = this.props;
    const { location: { pathname } } = history;
    if (['/sign-in', '/unauthorized'].indexOf(pathname) === -1) {
      if (pathname !== '/') {
        dispatch(rememberPathBeforeLogin(pathname));
      }
      externalLogin();
    }
    return null;
  }

  showHeader() {
    const { isAuthenticated, settings, authState } = this.props;
    const { location: { pathname } } = history;

    return authState !== AuthState.Unauthorised
      && isAuthenticated
      && settings && settings.clients && (settings.clients.length === 1 || (settings.selectedClient != null))
      && ['/unauthorized'].indexOf(pathname) === -1;
  }

  async preloadCheck() {
    const { dispatch } = this.props;
    try {
      const response = await fetch('./config/preload.json');
      if (!response.ok) {
        if (response.status === 404) {
          dispatch(getSiteConfig());
        } else {
          throw new Error(`Unexpected error getting preload.json. Status: ${ response.status }`);
        }
      }
      const jsonData = await response.json();
      dispatch(getSitePreload(jsonData));
    } catch (ex) {
      console.error('Unexpected error getting preload.json', ex);
    }
  }

  render() {
    const {
      dispatch,
      authMode,
      authState,
      isAuthenticated,
      username,
      permissions,
      settings,
      isFetchingUserConfig
    } = this.props;

    return (
      <ConnectedRouter history={ history }>
        <div className="root-container">
          { this.showHeader() && <Header
            username={ username }
            settings={ settings }
            permissions={ permissions }
            dispatch={ dispatch }
          /> }

          <ConnectionStatus>We have detected that your internet connection is unavailable.<br />Please wait as we attempt to re-establish a connection.</ConnectionStatus>

          <main>
            { !isAuthenticated && this.isUnauthorized && <UnauthorizedPage /> }
            { authState === AuthState.LoggedOut && this.redirectToLogin() }

            { !this.isSiteLoaded && !this.isSiteDown && !this.siteHasNoClients && !this.isUnlicensed && !this.isUnauthorized && <SiteLoading /> }
            { this.isUnlicensed && <SiteLoading isLoaded className="error" title="Access Not Granted" caption="You have not been permissioned to access this module. Please contact your vendor manager." /> }
            { this.isSiteDown && <SiteLoading isLoaded className="error" title="Platform Access Not Activated" caption="Your access to the TAINA platform has not yet been activated. Please contact your vendor manager." /> }
            { this.siteHasNoClients && <SiteLoading isLoaded className="error" caption="Sorry, the system has no active clients configured.  Please contact your system administrator." /> }

            { this.isSiteLoaded && authMode === AuthMode.Manual && !isAuthenticated && this.redirectToLogin() }
            { this.isSiteLoaded && authMode !== AuthMode.Manual
              && authState !== AuthState.Unauthorised && authState !== AuthState.LoggedOut && !isAuthenticated && this.redirectToSSO() }

            { this.isSiteLoaded && !isAuthenticated && <Switch><Route path="/sign-in" component={ Login } /></Switch> }

            { this.isAuthenticatedUser && this.proceedWithClientSelection() }

            { this.isAuthenticatedUser && settings && !settings.selectedClient && <Switch><Route path="/client-select" component={ ClientSelect } /></Switch> }

            { this.isAuthenticatedUser && isFetchingUserConfig && <SiteLoading /> }

            { this.isAuthenticatedUser && settings && settings.selectedClient && !isFetchingUserConfig && <Switch>
              <Route path="/sign-in" component={ Login } />
              <Route exact path="/" component={ Home } />
              { AccessControl.hasPermission(
                permissions,
                [ActivityPerms.scannedView,
                  ActivityPerms.scannedEdit,
                  ActivityPerms.scannedValidate,
                  ActivityPerms.electronicView,
                  ActivityPerms.electronicValidate]
              ) && this.clientIsSet
                ? <Route exact path="/forms/:blankType/:formId" component={ Blank } />
                : <Route exact path="/forms/:blankType/:formId" component={ UnauthorizedPage } /> }
              { AccessControl.hasPermission(permissions, [ActivityPerms.scannedDownload])
                && this.clientIsSet
                ? <Route exact path="/forms/:blankType/:formId/pdf" component={ OriginalPdfForm } />
                : <Route exact path="/forms/:blankType/:formId/pdf" component={ UnauthorizedPage } /> }
              <Route
                path="/validation-results"
                render={ () => (
                  AccessControl.hasPermission(
                    permissions,
                    [ActivityPerms.electronicView,
                      ActivityPerms.scannedView]
                  ) ? <ValidationResults /> : <UnauthorizedPage />) }
              />
              <Route
                path="/digital-entry"
                render={ () => {
                  if (settings && !settings.showDigitalEntry) {
                    return <UnlicensedPage />;
                  }
                  if (AccessControl.hasPermission(permissions, [ActivityPerms.digitalEntryAccessPortal, ActivityPerms.fwfInitialReviewer], true)) {
                    return <OnlineForms />;
                  }
                  return <UnauthorizedPage />;
                } }
              />
              <Route
                path="/whs-management"
                render={ () => {
                  if (settings && !settings.showMultiTierManager) {
                    return <UnlicensedPage />;
                  }
                  if (AccessControl.hasPermission(permissions, [ActivityPerms.multiTierMgrViewWhs, ActivityPerms.multiTierMgrEditWhs])) {
                    return <WhsManagement />;
                  }
                  return <UnauthorizedPage />;
                } }
              />
              <Route
                path="/advanced-search"
                render={ () => {
                  if (settings && !settings.showAdvancedSearch) {
                    return <UnlicensedPage />;
                  }
                  if (AccessControl.hasPermission(permissions, [ActivityPerms.scannedAdvancedSearch])) {
                    if (settings && !settings.isMultiDb) {
                      return <AdvancedSearch />;
                    }
                    return <AdvancedSearchMultiDb />;
                  }
                  return <UnauthorizedPage />;
                } }
              />
              <Route
                exact
                path="/w8imystructure/:accountId/:versionNumber?"
                render={ () => {
                  if (AccessControl.hasPermission(permissions, [ActivityPerms.scannedViewTree]) && this.clientIsSet) {
                    return <VisualiseW8Imy />;
                  }
                  return <UnauthorizedPage />;
                } }
              />
              <Route
                exact
                path="/whd-statement/:accountId/:versionNumber?"
                render={ () => {
                  if (AccessControl.hasPermission(permissions, [ActivityPerms.multiTierMgrViewWhs]) && this.clientIsSet) {
                    return <WhdStatement />;
                  }
                  return <UnauthorizedPage />;
                } }
              />
              <Route path="/client-select" render={ () => <ClientSelect /> } />
              <Route path="/account/:id" render={ () => (this.clientIsSet && AccessControl.hasPermission(permissions, [ActivityPerms.accountEdit, ActivityPerms.accountPageView]) ? <AccountPage /> : <UnauthorizedPage />) } />
              <Route path="/customer/add" exact render={ () => (this.clientIsSet && AccessControl.hasPermission(permissions, [ActivityPerms.customerEdit, ActivityPerms.accountEdit]) ? <AddCustomerAccount /> : <UnauthorizedPage />) } />
              <Route path="/customer/:id" render={ () => (this.clientIsSet && AccessControl.hasPermission(permissions, [ActivityPerms.customerEdit, ActivityPerms.customerPageView]) ? <CustomerPage /> : <UnauthorizedPage />) } />
              <Route path="/mi-dashboard" render={ () => (AccessControl.hasPermission(permissions, [ActivityPerms.miView]) && this.clientIsSet ? <MIDashboard /> : <UnauthorizedPage />) } />
              <Route
                path="/batches/:batchId/files/:batchFileId"
                render={ () => {
                  if (settings && !settings.showBatches) {
                    return <UnlicensedPage />;
                  }
                  if (AccessControl.hasPermission(permissions, [ActivityPerms.scannedViewBatches]) && this.clientIsSet) {
                    return <BatchFormsContainer />;
                  }
                  return <UnauthorizedPage />;
                } }
              />
              <Route
                path="/batches/:batchId/forms"
                render={ () => {
                  if (settings && !settings.showBatches) {
                    return <UnlicensedPage />;
                  }
                  if (AccessControl.hasPermission(permissions, [ActivityPerms.scannedViewBatches]) && this.clientIsSet) {
                    return <BatchFormsContainer />;
                  }
                  return <UnauthorizedPage />;
                } }
              />
              <Route
                path="/batches/:batchId"
                render={ () => {
                  if (settings && !settings.showBatches) {
                    return <UnlicensedPage />;
                  }
                  if (AccessControl.hasPermission(permissions, [ActivityPerms.scannedViewBatches]) && this.clientIsSet) {
                    return <BatchFilesContainer />;
                  }
                  return <UnauthorizedPage />;
                } }
              />
              <Route
                path="/batches"
                render={ () => {
                  if (settings && !settings.showBatches) {
                    return <UnlicensedPage />;
                  }
                  if (AccessControl.hasPermission(permissions, [ActivityPerms.scannedViewBatches]) && this.clientIsSet) {
                    return <BatchesContainer />;
                  }
                  return <UnauthorizedPage />;
                } }
              />
              <Route path="/customers/new/:id?" render={ () => (settings && settings.showCustomerOnboarding && this.clientIsSet ? <Questionnaire /> : <UnauthorizedPage />) } />
              <Route
                exact
                path="/customers/supporting-docs/:docId"
                render={ routeProps => (AccessControl.hasPermission(permissions, [ActivityPerms.scannedDownload], true)
                  && this.clientIsSet
                  ? <SupportingDocumentViewer { ...routeProps } />
                  : <UnauthorizedPage />) }
              />
              <Route
                path="/monitoring"
                render={ () => {
                  if (settings && !settings.showOngoingMonitoring) {
                    return <UnlicensedPage />;
                  }
                  if (AccessControl.hasPermission(permissions, [ActivityPerms.monitoringViewUnassigned, ActivityPerms.monitoringViewUnresolved,
                    ActivityPerms.monitoringViewResolved, ActivityPerms.monitoringExpertReviewer, ActivityPerms.monitoringResolver]) && this.clientIsSet) {
                    return <Monitoring />;
                  }
                  return <UnauthorizedPage />;
                } }
              />
              <Route path="/unauthorized" component={ UnauthorizedPage } />
              <Route path="/nointernet" component={ NoInternetPage } />
              <Route component={ NotFoundPage } />
            </Switch> }
          </main>
          <ReduxToastr
            preventDuplicates
            position="top-right"
            transitionIn="fadeIn"
            transitionOut="fadeOut"
          />
        </div>
      </ConnectedRouter>
    );
  }
}

const mapStateToProps = (state) => {
  const { auth } = state;
  const { siteReady, authMode, authState, sessionAuthMode, isAuthenticated, username, permissions, settings, isFetchingUserConfig } = auth;

  return {
    siteReady,
    authMode,
    authState,
    sessionAuthMode,
    isAuthenticated,
    username,
    permissions,
    settings,
    isFetchingUserConfig
  };
};

const mapDispatchToProps = dispatch => ({ dispatch });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withAppErrorBoundary(App, 'App'));
